module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('title', [_vm._v("Redeeming Transcendence in the Arts: Bearing Witness to the Triune God")]), _vm._v(" "), _c('link', {
    attrs: {
      "href": "../styles/stylesheet.css",
      "rel": "stylesheet",
      "type": "text/css"
    }
  }), _vm._v(" "), _c('p', {
    staticClass: "OT",
    attrs: {
      "id": "int"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_1"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/Contents#int1"
    }
  }, [_vm._v("Introduction")])]), _vm._v(" "), _c('p', {
    staticClass: "CE"
  }, [_c('i', [_vm._v("An urge for transcendence is what makes most people want to write, paint, sculpt, or dance in the first place.")])]), _vm._v(" "), _c('p', {
    staticClass: "CC"
  }, [_vm._v("—Bill Friskics-Warren, "), _c('i', [_vm._v("I’ll Take You There")])]), _vm._v(" "), _c('p', {
    staticClass: "border"
  }), _c('p', {
    staticClass: "Bodyflush"
  }, [_vm._v("One of the more striking features of the current conversation between theology and the arts is the pervasiveness of talk of transcendence."), _c('a', {
    attrs: {
      "id": "fn1"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn1"
    }
  }, [_vm._v("1")])]), _vm._v(" For many, the arts powerfully signal realities exceeding what we can know and tell. In fact, the gut intuition that there is a special link between art and a transcendent “beyond” is ancient. The very business of fashioning art involves reaching beyond our immediate circumstances, and "), _c('a', {
    attrs: {
      "id": "page_2"
    }
  }, [_vm._v("many have described “great” art as inspired by a force or spirit not of this world. Today it is not hard to find the arts allied to the “spiritual” or “spirituality,” words that, however slippery, seem to carry an air of transcendence, divine or otherwise. The Princeton sociologist Robert Wuthnow, after processing over four hundred interviews with church members, clergy, and directors of leading arts organizations, concludes that the arts seem to “serve as a common means of encouraging and expressing experiences of transcendence.”")]), _c('a', {
    attrs: {
      "id": "fn2"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn2"
    }
  }, [_vm._v("2")])]), _vm._v(" In another place he speaks of how a large number of artists appear to believe that “they are providing small experiences of transcendence that in themselves become reasons for hope. It is the creative process itself that momentarily transcends time, offering an awareness that something other than the ordinary can exist and, through that existence, reinvigorate the flawed aspirations of ordinary people.”"), _c('a', {
    attrs: {
      "id": "fn3"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn3"
    }
  }, [_vm._v("3")])]), _vm._v(" Not surprisingly, this readiness of artists to talk of transcendence has been noticed by many scholars and academics, some of whom have taken the issue in explicitly theological directions.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("It is my purpose in this book to ask: What kind of theological weight can be given to the language of divine transcendence when it is associated with the arts? More succinctly: How, if at all, might the arts bear their own kind of witness to divine transcendence?")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Many are reluctant to give any serious theological credence to “transcendence-talk” in connection with the arts, given the fluidity of terms often used and the kind of aesthetic special pleading that seems to swerve dangerously close to idolatry. As for myself, I am not convinced a wholesale skepticism is in order. I have come to believe that there is something "), _c('a', {
    attrs: {
      "id": "page_3"
    }
  }, [_vm._v("worth exploring seriously here—indeed, something that the church needs to address if it is to exercise an effective and winsome witness in our contemporary cultural moment. At the same time, when it comes to interpreting and evaluating such purported “rumors of transcendence” in the arts, I have also come to believe that we have often been doing so with theological tools that are distinctly blunt and impotent (and are sometimes simply the wrong tools). In a proper eagerness to make common cause with those of little or no explicit faith, we have too frequently lost a sense of the disturbing particularities and disruptive power of the vistas presented in the Old and New Testaments. I will argue that if we pay greater attention to the highly distinctive contours opened up by a “scriptural imagination” and the creedal traditions that resonate with them, we will be led to question at least some of the more common ways transcendence is being related to the arts today, and discover far richer resources for engaging constructively what is undoubtedly a widespread conviction that the two are in some sense profoundly linked.")])]), _c('a', {
    attrs: {
      "id": "page_3"
    }
  }, [_c('p', {
    staticClass: "Body"
  }, [_vm._v("I am not implying that we should close down conversations with those who count themselves outside the Christian church but who find in the arts a source of ineffable wonder and pleasure. Quite the opposite. For the last twenty years or so, I have been involved in hundreds of conversations of this sort. But my experience has convinced me that most is gained when we refuse to disregard or downplay the primary sources that ground the church’s faith, and the kind of worldview they invite us to live into and live out in the world. Indeed, one of the main burdens of this book is to challenge the assumption that focusing on the stubborn, historically grounded particularities of biblical faith (and biblically shaped doctrine) will inevitably weaken our ability to engage with appeals to divine transcendence in our culture at large.")])]), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_3"
    }
  }, [_vm._v("Not surprisingly, in this book we will struggle with language, ")]), _c('a', {
    attrs: {
      "id": "page_4"
    }
  }, [_vm._v("and most of all with the term “transcendence” itself. The theologian Nicholas Lash has spoken of the church as “an academy of word-care.”")]), _c('a', {
    attrs: {
      "id": "fn4"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn4"
    }
  }, [_vm._v("4")])]), _vm._v(" But examining and re-forming terms and the concepts that go with them are only part of the task. As an army of philosophers remind us, language is always language-in-use, embedded in the many-layered densities of practical and communal life. We need to be alert not only to language in and of itself but also to the work our language does (and could do) amidst the arts. What matters in the long run is the way the arts are "), _c('i', [_vm._v("done")]), _vm._v(", what we "), _c('i', [_vm._v("do")]), _vm._v(" with them, and what "), _c('i', [_vm._v("happens")]), _vm._v(" through them. This will be especially important in our final chapter: the renewal of terms is only one part of the job and, in the long run, fairly pointless if not enacted in fresh interpretation of the arts and renewed artistic activity.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("In case there is confusion, my chief interest is not in what are commonly known as “the transcendentals”—classically truth, goodness, and beauty. Attempts to relate this trio and its many variants to the arts are not hard to find these days (beauty is enjoying special favor at present). But my concern here is first and foremost with divine transcendence, the transcendence of the God evoked in Scripture, and how this might affect our involvement with the arts. I have no wish to discard out of hand accounts of the arts as bearers of the so-called transcendentals (or ideals, universals, and so forth), nor do I wish to suggest that the transcendentals (in whatever version) have no theological significance. But I do question the wisdom of orienting a discussion of divine transcendence in the arts around philosophical traditions of this sort a priori, lest the singular perspectives opened up by Scripture and carried forward in the church’s traditions are muffled or skewed, and thus not allowed to exercise their distinctive power.")]), _vm._v(" "), _c('p', {
    staticClass: "H1"
  }, [_c('a', {
    attrs: {
      "id": "page_5"
    }
  }, [_vm._v("Transcendence?")])]), _c('a', {
    attrs: {
      "id": "page_5"
    }
  }), _c('p', {
    staticClass: "Bodyflush1"
  }, [_c('a', {
    attrs: {
      "id": "page_5"
    }
  }, [_vm._v("To state the obvious: “transcendence,” “transcendent,” and their cognates can be used in a bewildering variety of senses; the word group has a long and knotty history.")]), _c('a', {
    attrs: {
      "id": "fn5"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn5"
    }
  }, [_vm._v("5")])]), _vm._v(" At the very least, it is clear that “transcendent” signals what lies “beyond” something “here,” but the nature of the “what” and the “beyond” (perhaps even the “here”) can take on a confusing assortment of meanings, many of them highly unstable. This can cause a good deal of frustration if we want to get some kind of purchase on why such vocabulary might be at all important, in the arts or anywhere else."), _c('a', {
    attrs: {
      "id": "fn6"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn6"
    }
  }, [_vm._v("6")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Nonetheless, to start at the most basic level, transcendence is obviously a correlative or relational term; it presupposes a reality to be transcended, as well as some kind of limit or boundary to that reality. To say X is transcendent is to say X transcends, or is transcendent in relation to Y."), _c('a', {
    attrs: {
      "id": "fn7"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn7"
    }
  }, [_vm._v("7")])]), _vm._v(" In theology the most significant and decisive meanings of transcendence relate to God: above all, God’s transcendence of the created world. We may distinguish two very broad senses (or families of senses). The first concerns God’s "), _c('i', [_vm._v("otherness")]), _vm._v(": in orthodox Jewish and Christian thought, God is transcendent in that there is an irreducible ontological distinction between Creator and "), _c('a', {
    attrs: {
      "id": "page_6"
    }
  }, [_vm._v("the creation, between God and all that is not God. God is not the world, the world is not God. The second concerns God’s "), _c('i', [_vm._v("uncontainability")]), _vm._v(": God is transcendent in that God cannot be circumscribed or encompassed, grasped or held by what is not God—and this includes, of course, human language and thought. These two senses will occupy the bulk of our attention in this book.")])]), _c('a', {
    attrs: {
      "id": "page_6"
    }
  }), _c('p', {
    staticClass: "Body"
  }, [_c('a', {
    attrs: {
      "id": "page_6"
    }
  }, [_vm._v("Some writers discriminate between “vertical” and “horizontal” (or “lateral”) transcendence, the former having to do with the God-world relation, the latter with immanent, this-worldly relations (with other humans perhaps, or the nonhuman world).")]), _c('a', {
    attrs: {
      "id": "fn8"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn8"
    }
  }, [_vm._v("8")])]), _vm._v(" Examples of horizontal transcendence could include self-transcendence (e.g., the transcendence of a person over her circumstances), or the transcendence of another person’s integrity over my own, or the transcendence of an ethical ideal believed to apply to all times and places."), _c('a', {
    attrs: {
      "id": "fn9"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn9"
    }
  }, [_vm._v("9")])]), _vm._v(" Art is sometimes said to provide instances of horizontal transcendence: for example, when it offers a vision of what human life could be in the future, or when making art is seen as a vehicle of self-transcendence. This book is in the first place theologically oriented; that is, our primary interest is in divine (vertical) transcendence. Even where we do touch on horizontal transcendence, our interest will be mainly theological, that is to say, related to the transcendence of the Christian God.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("In addition, some will want to distinguish “eschatological transcendence” as a distinct type: the transcendence of the future over the present, or, in biblical terms, the transcendence of the new creation over the old. This eschatological, forward thrust is undoubtedly crucial for expounding God’s "), _c('a', {
    attrs: {
      "id": "page_7"
    }
  }, [_vm._v("transcendence in scriptural terms, and I will have much to say about it later. But strictly speaking, this is not a form of divine transcendence but rather the transcendence of one temporal condition over another.")])]), _c('a', {
    attrs: {
      "id": "page_7"
    }
  }, [_c('p', {
    staticClass: "H1"
  }, [_vm._v("Postsecularity and Suspicion")])]), _c('p', {
    staticClass: "Bodyflush1"
  }, [_c('a', {
    attrs: {
      "id": "page_7"
    }
  }, [_vm._v("Two other broad matters need to be kept in mind throughout. First, it is hard not to believe that the current renewed interest in the arts and transcendence is bound up with a shift of sensibility in Western culture that goes under various names: “secularization,” “disenchantment,” “desacralization,” the emergence of “the immanent frame.”")]), _c('a', {
    attrs: {
      "id": "fn10"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn10"
    }
  }, [_vm._v("10")])]), _vm._v(" In its starkest, cosmological form, the outcome is a way of imagining the physical world as utterly devoid of the presence and activity of any deity, indeed, any extraworldly reality."), _c('a', {
    attrs: {
      "id": "fn11"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn11"
    }
  }, [_vm._v("11")])]), _vm._v(" Among its political variations have been the notion of the “secular state,” and political arrangements not tied to any particular religious affiliation. This complex and much-debated phenomenon has generated a range of responses, including, it would seem, a "), _c('a', {
    attrs: {
      "id": "page_8"
    }
  }, [_vm._v("fresh fascination with the notion of transcendence, and often among those with no formal or direct affiliation to a religious faith or institution (who, it is sometimes said, incline more to the “spiritual” than to the “religious”).")]), _c('a', {
    attrs: {
      "id": "fn12"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn12"
    }
  }, [_vm._v("12")])]), _vm._v(" Some even speak of a recent “turn” to transcendence,"), _c('a', {
    attrs: {
      "id": "fn13"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn13"
    }
  }, [_vm._v("13")])]), _vm._v(" seen in various efforts to come to terms with, or compensate for, the perceived loss of traditional theological transcendence, and ranging across a myriad of disciplines, including philosophy, cultural studies, linguistics, phenomenology, and—not least—theology."), _c('a', {
    attrs: {
      "id": "fn14"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn14"
    }
  }, [_vm._v("14")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("It is not difficult to see how the arts might prove appealing in this “postsecular” climate. If one is faced with a barren physicalism that regards the entire material environment we inhabit as amenable to exhaustive explanation by the natural sciences, or with a tacit belief in the eventual human domination of the physical world through ever greater technological control, or with a suspicion of anything that cannot be subsumed under what is thought to be strict scientific reasoning, then the arts will look mightily attractive. For they seem to provide a means of restoring at least some of what was so attractive about a premodern outlook—a sense of awe, wonder, ambiguity or mystery perhaps, a recognition of forces at work in our world that elude the grasp of instrumental reason. The Romantic movement, with its reaction to a certain hue of Enlightenment rationalism and its immense elevation of the arts, is undoubtedly a critical part of the background here."), _c('a', {
    attrs: {
      "id": "fn15"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn15"
    }
  }, [_vm._v("15")])]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_9"
    }
  }, [_vm._v("Adrienne Dengerink Chaplin speaks of the way “the quest for the spiritual” in twentieth-century abstract painting was primarily motivated by “a reaction to the perceived soulless functionality of the modern industrialized world, together with the suffocating character of its bourgeois morality, and the oppressive dogmatism of its institutional religion.”")]), _c('a', {
    attrs: {
      "id": "fn16"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn16"
    }
  }, [_vm._v("16")])]), _vm._v(" For the painter Wassily Kandinsky, writes Donald Kuspit, “non-objective art is the only means of transcendence—the "), _c('i', [_vm._v("only")]), _vm._v(" means of transcendence—of the objective, practical modern world.”"), _c('a', {
    attrs: {
      "id": "fn17"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn17"
    }
  }, [_vm._v("17")])]), _vm._v(" When the art critic Suzi Gablik wrote over twenty years ago about the capacity of art to reenchant our culture, she was articulating what has in fact been a pervasive strain in modernity."), _c('a', {
    attrs: {
      "id": "fn18"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn18"
    }
  }, [_vm._v("18")])]), _vm._v(" And there are those convinced that the arts have a key role vis-à-vis what has been called the SBNR (“spiritual but not religious”) phenomenon today."), _c('a', {
    attrs: {
      "id": "fn19"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn19"
    }
  }, [_vm._v("19")])]), _vm._v(" Manav Ratti, with an Indian and Sri Lankan context in mind, points to the potential of literature to resacralize our world:")]), _vm._v(" "), _c('p', {
    staticClass: "EX"
  }, [_vm._v("Literature is powerfully poised to demonstrate the undoing of the ideological oppositions between secularism and religion, for in its ability to represent a multiplicity of voices "), _c('a', {
    attrs: {
      "id": "page_10"
    }
  }, [_vm._v("and in its acceptance and juxtaposition of contradictory and conflicting perspectives, it can represent, imagine and pursue a rich array of possibilities. The postsecular possibilities that writers can gesture toward through literature are not anti-secular, nor are they abandoning secularism or turning to religion. The postsecular neither proselytizes secularism nor sentimentalizes religion. It can recognize that enchantment is not the provision of religion alone, and can tackle the hard questions of the political while acknowledging the dimensions of religion.")]), _c('a', {
    attrs: {
      "id": "fn20"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn20"
    }
  }, [_vm._v("20")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body1"
  }, [_vm._v("This is related to a second matter to highlight. Most of those who point to the arts as potential sites of transcendence are acutely aware of what one writer has called the “unsavoury reputation” that transcendence has picked up in late modernity."), _c('a', {
    attrs: {
      "id": "fn21"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn21"
    }
  }, [_vm._v("21")])]), _vm._v(" It drags with it a blood-stained history. To claim that some truth or authority subsists above all times and places—and, worse still, to presume that we can gain some kind of stable access to that truth or authority—all too easily leads to coercive oppression. How can we ignore the horrors perpetrated by murderous dictators and fanatical jihadists who claim to deliver “transcendent” certainties? A long line of cultural critique reminds us that theological transcendence and tyranny have a nasty habit of joining hands. Feminist theologians are quick to point out that gender can be pulled into these dynamics with disturbing ease: transcendence becomes aligned to a certain model of male identity and hence to a God imaged as disembodied, omniscient, omnipotent, and omnipresent, operating in an imperialistic, top-down (and arbitrary?) manner. Highly destructive power "), _c('a', {
    attrs: {
      "id": "page_11"
    }
  }, [_vm._v("moves follow, perhaps most obviously the male denigration of women.")]), _c('a', {
    attrs: {
      "id": "fn22"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn22"
    }
  }, [_vm._v("22")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("In this light, the major monotheistic faiths will often be viewed as part of the problem, not the solution. We are told, for example, that the carnage and misery wreaked by Christians over the last two thousand years in the name of their deity are directly linked to the way the church has imagined divine transcendence. Hence it is not surprising to find various forms of “secularized” horizontal transcendence being advocated as an alternative to traditional models."), _c('a', {
    attrs: {
      "id": "fn23"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn23"
    }
  }, [_vm._v("23")])]), _vm._v(" In John Caputo’s words, transcendence becomes rehabilitated “for a more worldly life, where it must be refitted for a more material, gendered and planetary existence.”"), _c('a', {
    attrs: {
      "id": "fn24"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn24"
    }
  }, [_vm._v("24")])]), _vm._v(" This partially "), _c('a', {
    attrs: {
      "id": "page_12"
    }
  }, [_vm._v("explains why so many in the arts over the last few decades have been keen to employ the language of transcendence but eschew any traditional theological ontology or metaphysics associated with it. According to art theorist Thierry de Duve, “the best modern art . . . has endeavoured to re-define the essentially "), _c('i', [_vm._v("religious")]), _vm._v(" terms of humanism on "), _c('i', [_vm._v("belief-less")]), _vm._v(" bases.”")]), _c('a', {
    attrs: {
      "id": "fn25"
    }
  }), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn25"
    }
  }, [_vm._v("25")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("It is clear, then, that much more is at stake in tackling the issue of transcendence in the arts than tidying up philosophical or doctrinal niceties. If language is inseparably bound up with social practices, it will be implicit in the formation and deformation of those practices: the manner in which we invoke transcendence-talk may end up encouraging or precipitating destructive and alienating patterns of life, in the arts as much as anywhere else. To invest the arts with a false transcendence, to overestimate (or to underestimate for that matter) their transcendent potential, can only redound to our harm.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The structure of the book is relatively straightforward. To throw into relief the critical issues at stake, in the first chapter I examine one broad way in which the arts and transcendence are being linked today, looking at some key representative writers. In chapter 2, I argue that much light can be thrown "), _c('a', {
    attrs: {
      "id": "page_13"
    }
  }, [_vm._v("on this approach by setting it in the context of a wide swathe of thought encapsulated in the concept of the sublime. By way of contrast, chapter 3 asks what it would mean to interpret transcendence through a “scriptural imagination” and creedal traditions that resonate with it—attending above all to what has been enacted climactically in Jesus Christ, and just because of this, to a God who is irreducibly triune. To close, in chapter 4, I return to the artistic arena and explore how this more biblically shaped vista might begin to reconfigure the way we envision the arts as potential witnesses to divine transcendence.")])]), _c('a', {
    attrs: {
      "id": "page_13"
    }
  }, [_c('p', {
    staticClass: "Body"
  }, [_vm._v("A final word by way of introduction. In writing this book, I have found myself fairly critical of some positions being recommended in the theology and arts field today. Many who hold these positions are my valued friends and colleagues. I hope it will be obvious that what I have written is intended constructively, not to close down but to open up conversation, and with gratitude for the considerable amount that I have learned from those with whom I disagree most sharply.")]), _vm._v(" "), _c('p', {
    staticClass: "line"
  }, [_c('img', {
    attrs: {
      "alt": "line",
      "src": "images/line1.jpg"
    }
  })])]), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn1"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_13"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/#fn1"
    }
  }, [_vm._v("1.")]), _vm._v(" The literature is legion, but see, for example, Kutter Callaway, "), _c('i', [_vm._v("Scoring Transcendence: Film Music as Contemporary Religious Experience")]), _vm._v(" (Waco: Baylor University Press, 2013); Douglas E. Cowan, "), _c('i', [_vm._v("Sacred Space: The Quest for Transcendence in Science Fiction Film and Television")]), _vm._v(" (Waco: Baylor University Press, 2010); Bill Friskics-Warren, "), _c('i', [_vm._v("I’ll Take You There: Pop Music and the Urge for Transcendence")]), _vm._v(" (New York: Continuum, 2005); Adrienne Dengerink Chaplin, “Transcendence Re-Mixed: On the Complex Role of Religion in Contemporary Art,” in "), _c('i', [_vm._v("Culture and Transcendence: A Typology of Transcendence")]), _vm._v(", ed. W. L. van der Merwe and Wessel Stoker (Leuven: Peeters, 2012), 163–78; Jennifer Anna Gosetti-Ferencei, “Immanent Transcendence in Rilke and Stevens,” "), _c('i', [_vm._v("German Quarterly")]), _vm._v(" 83, no. 3 (2010): 275–96; Gordon Graham, “Enchantment and Transcendence: David Brown on Art and Architecture,” in "), _c('i', [_vm._v("Theology, Aesthetics, and Culture: Responses to the Work of David Brown")]), _vm._v(", ed. Robert MacSwain and Taylor Worley (Oxford: Oxford University Press, 2012), 91–102.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn2"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn2"
    }
  }, [_vm._v("2.")]), _vm._v(" Robert Wuthnow, "), _c('i', [_vm._v("All in Sync: How Music and Art Are Revitalizing American Religion")]), _vm._v(" (Berkeley: University of California Press, 2003), 77.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn3"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn3"
    }
  }, [_vm._v("3.")]), _vm._v(" Robert Wuthnow, "), _c('i', [_vm._v("Creative Spirituality: The Way of the Artist")]), _vm._v(" (Berkeley: University of California Press, 2001), 262.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn4"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn4"
    }
  }, [_vm._v("4.")]), _vm._v(" Nicholas Lash, “Ministry of the Word or Comedy and Philology,” "), _c('i', [_vm._v("New Blackfriars")]), _vm._v(" 68 (1987):472–83, here 477.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn5"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn5"
    }
  }, [_vm._v("5.")]), _vm._v(" For a perceptive treatment of different senses of transcendence, see Ingolf Dalferth, “The Idea of Transcendence,” in "), _c('i', [_vm._v("The Axial Age and Its Consequences")]), _vm._v(", ed. Robert N. Bellah and Hans Joas (Cambridge, MA: Belknap Press of Harvard University Press, 2012), 146–88.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn6"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn6"
    }
  }, [_vm._v("6.")]), _vm._v(" See, for example, the cloudiness and irresolution of a recent symposium on “religion and art” among leading practitioners and theorists. James Elkins and David Morgan, "), _c('i', [_vm._v("Re-Enchantment")]), _vm._v(" (New York: Routledge, 2009). Note Elkins’s distinctly downbeat comments on 110–11.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn7"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn7"
    }
  }, [_vm._v("7.")]), _vm._v(" This is easily forgotten in some of the present-day debates, when, for example, it is tacitly assumed that when we use the phrase “the transcendent” we are of necessity referring to some kind of entity or object. This leads to many needless philosophical and theological muddles.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn8"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn8"
    }
  }, [_vm._v("8.")]), _vm._v(" Linda A. Mercadante, "), _c('i', [_vm._v("Belief without Borders: Inside the Minds of the Spiritual but Not Religious")]), _vm._v(" (New York: Oxford University Press, 2014), 94.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn9"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn9"
    }
  }, [_vm._v("9.")]), _vm._v(" John D. Caputo and Michael J. Scanlon, eds., "), _c('i', [_vm._v("Transcendence and Beyond: A Postmodern Inquiry")]), _vm._v(" (Bloomington: Indiana University Press, 2007), 3–4, and chs. 7–11.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn10"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn10"
    }
  }, [_vm._v("10.")]), _vm._v(" See, e.g., Charles Taylor, “A Place for Transcendence?,” in "), _c('i', [_vm._v("Transcendence: Philosophy, Literature, and Theology Approach the Beyond")]), _vm._v(", ed. Regina M. Schwartz (New York: Routledge, 2004), 1–11; Taylor, "), _c('i', [_vm._v("A Secular Age")]), _vm._v(" (Cambridge, MA: Harvard University Press, 2007); Brad S. Gregory, "), _c('i', [_vm._v("The Unintended Reformation: How a Religious Revolution Secularized Society")]), _vm._v(" (Cambridge, MA: Belknap Press of Harvard University Press, 2012); Steven D. Smith, "), _c('i', [_vm._v("The Disenchantment of Secular Discourse")]), _vm._v(" (Cambridge, MA: Harvard University Press, 2010).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn11"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn11"
    }
  }, [_vm._v("11.")]), _vm._v(" According to Charles Taylor, part of what distinguishes our late modern culture from premodern medieval culture is that belief in God is typically regarded as little more than a private option among others, of no intrinsic connection to matters of public truth: what he calls “exclusive humanism.” Taylor, "), _c('i', [_vm._v("A Secular Age")]), _vm._v(", 19–21. This is characterized by James K. A. Smith as “a way of constructing meaning and significance without any reference to the divine or transcendence.” James K. A. Smith, "), _c('i', [_vm._v("How (Not) to Be Secular: Reading Charles Taylor")]), _vm._v(" (Grand Rapids: Eerdmans, 2014), 26.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn12"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn12"
    }
  }, [_vm._v("12.")]), _vm._v(" Mercadante, "), _c('i', [_vm._v("Belief without Borders")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn13"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn13"
    }
  }, [_vm._v("13.")]), _vm._v(" Glenn W. Olsen, "), _c('i', [_vm._v("The Turn to Transcendence: The Role of Religion in the Twenty-First Century")]), _vm._v(" (Washington, DC: Catholic University of America Press, 2010).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn14"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn14"
    }
  }, [_vm._v("14.")]), _vm._v(" See, e.g., Wessel Stoker and W. L. van der Merwe, eds., "), _c('i', [_vm._v("Looking Beyond? Shifting Views of Transcendence in Philosophy, Theology, Art, and Politics")]), _vm._v(" (Amsterdam: Rodopi, 2012); Cowan, "), _c('i', [_vm._v("Sacred Space")]), _vm._v("; Conor Cunningham and Peter M. Candler, eds., "), _c('i', [_vm._v("Transcendence and Phenomenology")]), _vm._v(" (London: SCM, 2007); Caputo and Scanlon, "), _c('i', [_vm._v("Transcendence and Beyond")]), _vm._v("; Schwartz, "), _c('i', [_vm._v("Transcendence")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn15"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn15"
    }
  }, [_vm._v("15.")]), _vm._v(" As David Morgan summarizes it, “Romantic writers and artists believed that art and nature could produce a bracing experience of what was variously called the infinite, the sublime, the timeless, or the divine. Artists were geniuses who had access to such domains and needed to be championed against the incursions of bourgeois sensibility, orthodox religion, false taste, and the state.” David Morgan, “Enchantment, Disenchantment, Re-Enchantment,” in Elkins and Morgan, "), _c('i', [_vm._v("Re-Enchantment")]), _vm._v(", 15.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn16"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn16"
    }
  }, [_vm._v("16.")]), _vm._v(" Chaplin, “Transcendence Re-Mixed,” 164.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn17"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn17"
    }
  }, [_vm._v("17.")]), _vm._v(" Donald B. Kuspit, “Reconsidering the Spiritual in Art,” "), _c('i', [_vm._v("Blackbird: An Online Journal of Literature and the Arts")]), _vm._v(" 2, no. 1 (Spring 2003), "), _c('a', {
    attrs: {
      "href": "http://www.blackbird.vcu.edu/v2n1/gallery/kuspit_d/reconsidering_text.htm",
      "target": "_blank"
    }
  }, [_vm._v("http://www.blackbird.vcu.edu/v2n1/gallery/kuspit_d/reconsidering_text.htm")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn18"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn18"
    }
  }, [_vm._v("18.")]), _vm._v(" Suzi Gablik, "), _c('i', [_vm._v("The Reenchantment of Art")]), _vm._v(" (London: Thames and Hudson, 1991). For critical discussion, see Gordon Graham, "), _c('i', [_vm._v("The Re-Enchantment of the World: Art versus Religion")]), _vm._v(" (Oxford: Oxford University Press, 2007).")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn19"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn19"
    }
  }, [_vm._v("19.")]), _vm._v(" See, for example, E. Byron Anderson, “Music and Meaning for the ‘Spiritual but Not Religious,’” "), _c('i', [_vm._v("Liturgy")]), _vm._v(" 30, no. 3 (2015): 14–22.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn20"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn20"
    }
  }, [_vm._v("20.")]), _vm._v(" Manav Ratti, "), _c('i', [_vm._v("The Postsecular Imagination: Postcolonialism, Religion, and Literature")]), _vm._v(" (New York: Routledge, 2013), xxi.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn21"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn21"
    }
  }, [_vm._v("21.")]), _vm._v(" Schwartz, "), _c('i', [_vm._v("Transcendence")]), _vm._v(", vii.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn22"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn22"
    }
  }, [_vm._v("22.")]), _vm._v(" For discussion, see Rebekah Miles, "), _c('i', [_vm._v("The Bonds of Freedom: Feminist Theology and Christian Realism")]), _vm._v(" (Oxford: Oxford University Press, 2001), 20–23; Nancy Frankenberry, “Feminist Philosophy of Religion,” in Stanford Encyclopedia of Philosophy, Winter 2011 ed., substantive revision November 17, 2011, "), _c('a', {
    attrs: {
      "href": "http://plato.stanford.edu/archives/win2011/entries/feminist-religion/",
      "target": "_blank"
    }
  }, [_vm._v("http://plato.stanford.edu/archives/win2011/entries/feminist-religion/")]), _vm._v("; especially section 4 (“Feminist Reconstructions of Transcendence”). In her survey of “SBNRs” (“spiritual but not religious”), published in 2014, Linda Mercadante found that “Many interviewees did not like the idea or term ‘God’ because they connected it with negative masculine stereotypes, such as the demanding, difficult-to-satisfy father, or the capricious king who could just as well smite you as help you.” Mercadante, "), _c('i', [_vm._v("Belief without Borders")]), _vm._v(", 95.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn23"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn23"
    }
  }, [_vm._v("23.")]), _vm._v(" For an example, see Michael C. Kalton, “Green Spirituality: Horizontal Transcendence,” in "), _c('i', [_vm._v("Paths of Integrity, Wisdom, and Transcendence: Spiritual Development in the Mature Self")]), _vm._v(", ed. Melvin Miller (London: Routledge, 2000); available at "), _c('a', {
    attrs: {
      "href": "http://faculty.washington.edu/mkalton/green%20spir1.htm#_Toc61521666",
      "target": "_blank"
    }
  }, [_vm._v("http://faculty.washington.edu/mkalton/green%20spir1.htm#_Toc61521666")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn24"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn24"
    }
  }, [_vm._v("24.")]), _vm._v(" John D. Caputo and Michael J. Scanlon, “Introduction: Do We Need to Transcend Transcendence?,” in Caputo and Scanlon, "), _c('i', [_vm._v("Transcendence and Beyond")]), _vm._v(", 2. Mercadante, concluding her survey of SBNRs, comments: “It is possible to show how culturally Christian concepts, previously held by many Americans, are now transposed into alternative themes and held by the majority of these SBNR interviewees. By pairing them, one can see what is rejected and what is substituted. Again, some examples should help to make this clear. Starting with the idea of God, in the new ethos, God is transposed from transcendent actuality into the sacred or divine self. The ‘sovereignty’ or freedom of God becomes instead readily accessible, even impersonal, divine energy to be used by the individual as he or she sees fit, with progress nearly guaranteed. The Spirit becomes less an agent of God than self-generating personal intuition.” Mercadante, "), _c('i', [_vm._v("Belief without Borders")]), _vm._v(", 232.")]), _vm._v(" "), _c('p', {
    staticClass: "foot",
    attrs: {
      "id": "rfn25"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn25"
    }
  }, [_vm._v("25.")]), _vm._v(" Thierry de Duve, "), _c('i', [_vm._v("Look, One Hundred Years of Contemporary Art")]), _vm._v(", trans. Simon Pleasance and Fronza Woods (Brussels: Ludion, 2001), 14. In the same circle of ideas, Donald Kuspit, in the face of what he sees as a society trapped in a materialistic reductionism, espouses a thoroughly modern reconstrual of the language of transcendence, where transcendence turns out to be the transcendence of the human “spirit,” the irreducible freedom of humans over the determinizing, threatening forces of nature and society (i.e., horizontal transcendence). It is this that we find powerfully enacted, he believes, in Kandinsky—“a beacon of transcendence in dark materialistic times.” Kuspit, “Reconsidering the Spiritual in Art.”")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-46afe7d0","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/Introduction.vue
// module id = 75
// module chunks = 0